/** @jsx jsx */
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Box, Flex, jsx, Label } from 'theme-ui';
import CountrySelector from '../parcelFlow/CountrySelector';
import { locNavigate } from '../state/session';
import { CountryCode, localizedCountryNames, mostPopularCountries } from '../utils/countryCode';

export default ({ sxx }) => {
  const { t } = useTranslation();
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        justifyContent: 'flex-start',
        ...sxx,
      }}
    >
      <Label sx={{ color: 'secondary', mb: 2 }} id="most-common-label">
        {t('buyParcel.country.mostCommon')}
      </Label>
      <MostPopularList />
      <Label sx={{ color: 'secondary', my: 2 }}>{t('buyParcel.country.select')}</Label>
      <CountrySelector external={true} ignoreCountries={[...mostPopularCountries, CountryCode.Finland]} />
    </Flex>
  );
};

const MostPopularList = () => {
  const dispatch = useDispatch();
  const container = useRef();

  const handleSelectCountry = useCallback(
    (countryCode) => {
      dispatch(locNavigate('/osta-paketti', `?country=${countryCode}`));
    },
    [dispatch]
  );

  return (
    <Flex
      ref={container}
      sx={{
        flexDirection: ['column', null, 'row'],
      }}
      aria-labelledby="most-common-label"
    >
      {mostPopularCountries.map((countryCode) => {
        const country = localizedCountryNames.find((c) => c.code === countryCode);
        return (
          <Box
            key={country.code}
            className="country"
            onClick={() => handleSelectCountry(country.code)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSelectCountry(country.code);
              }
            }}
            sx={{
              flex: 'none',
              mr: 2,
              mb: 2,
            }}
            role="radio"
            aria-labelledby={`label-${country.code}`}
            tabIndex={0}
          >
            <CountryCard country={country} isSelected={false} />
          </Box>
        );
      })}
    </Flex>
  );
};

const CountryCard = ({ country: { code, name }, isSelected }) => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const localizedName = name[language] || name.fi;
  if (code === 'uk') {
    code = 'gb';
  }

  // flag images: npm i svg-country-flags
  return (
    <Flex
      sx={{
        position: 'relative',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: ['flex-start', null, 'center'],
        width: ['100%', null, 120],
        minHeight: [null, null, 160],
        maxHeight: 200,
        py: [1, null, 3],
        px: 2,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: isSelected ? 'primary' : 'border',
        borderRadius: 1,
        color: isSelected ? 'white' : 'inherit',
        backgroundColor: isSelected ? 'primary' : 'white',
        ':hover': {
          borderColor: isSelected ? 'primary' : 'grayLight',
          cursor: 'pointer',
        },
      }}
    >
      <Flex
        sx={{
          flexDirection: ['row', null, 'column'],
          alignItems: 'center',
          justifyContent: ['flex-start', null, 'center'],
          gap: 3,
        }}
      >
        <img
          src={'/flags/' + code.toLowerCase() + '.svg'}
          alt={'lippu ' + localizedName}
          sx={{ flexGrow: 1, height: 64, width: 64, my: 3 }}
          onError={(e) => (e.target.style = 'visibility: hidden')}
        />
        <span id={`label-${code}`} sx={{ fontWeight: 'medium', textAlign: 'center' }}>
          {localizedName}
        </span>
      </Flex>
    </Flex>
  );
};
